import {
  Filter,
  FilterOption,
  ListFilter,
  Operator
} from '../types'

import store from '../store.ts'
import Doc from '../api/Doc'
import { translate } from '../i18n.ts'

// Helpers
/////////////////////////////////////////////////

export function getFilterById(id: string): Filter {
  const filter = filters[id]
  if (!filter) {
    throw new Error(translate('error.Unknown filter id', { id: `"${id}"`}))
  }
  return filter
}

export function resolveListOptions(
  list: ListFilter,
  onlyAvailable: boolean
): FilterOption[] {
  let options = Array.isArray(list.options) ? list.options : list.options(store.state.account!)

  // Used by the filter editor to only show available options
  // and allow other places to reference all options that ever
  // existed.
  if (onlyAvailable && list.reduceToAvailable) {
    options = list.reduceToAvailable(options)
  }
  return options
}

export function getAvailableFilters(doc: Doc): Filter[] {
  return Object
    .keys(filters)
    .map(key => filters[key])
    .filter(d => {
      return !d.canUse || d.canUse(doc)
    })
}

// Operators
/////////////////////////////////////////////////

const matchOperators: Operator[] = [
  {
    value: 'LIKE',
    label: translate('filters.Matches list'),
    description: translate('filters.matchesListDescription')
  },
  {
    value: 'NOT_LIKE',
    label: translate('filters.Not matches list'),
    description: translate('filters.notMatchesListDescription')
  },
  {
    value: 'REGEX',
    label: translate('filters.Matches regexp'),
    description: translate('filters.matchesRegexpDescription')
  },
  {
    value: 'NOT_REGEX',
    label: translate('filters.Not matches regexp'),
    description: translate('filters.notMatchesRegexpDescription')
  }
]

const oneOfOperators: Operator[] = [
  {
    value: 'IN',
    label: translate('filters.Is one of'),
    description: translate('filters.isOneOfDescription')
  },
  {
    value: 'NOT_IN',
    label: translate('filters.Is not one of'),
    description: translate('filters.isNotOneOfDescription')
  }
]

const numericOperators: Operator[] = [
  {
    value: 'NOT_EQUALS',
    label: translate('filters.Is not equal to'),
    description: translate('filters.isNotEqualToDescription')
  },
  {
    value: 'EQUALS',
    label: translate('filters.Is equal to'),
    description: translate('filters.isEqualToDescription')
  },
  {
    value: 'GREATER_THAN',
    label: translate('filters.Is greater than'),
    description: translate('filters.IsGreaterThanDescription')
  },
  {
    value: 'LESS_THAN',
    label: translate('filters.Is less than'),
    description: translate('filters.IsLessThanDescription')
  },
  {
    value: 'GREATER_THAN_OR_EQUALS',
    label: translate('filters.Is greater than or equal to'),
    description: translate('filters.greaterThanOrEqualDescription')
  },
  {
    value: 'LESS_THAN_OR_EQUALS',
    label: translate('filters.Is less than or equal to'),
    description: translate('filters.lessThanOrEqualDescription')
  }
]

const existanceOperators: Operator[] = [
  {
    value: 'IS_DEFINED',
    label: translate('filters.Is defined'),
    description: translate('filters.isDefinedDescription')
  },
  {
    value: 'IS_NOT_DEFINED',
    label: translate('filters.Is not defined'),
    description: translate('filters.isNotDefinedDescription')
  }
]

// Filters (make sure you export them at the bottom)
/////////////////////////////////////////////////

const PAGE_URL_CONTAINS: Filter = {
  id: 'PAGE_URL_CONTAINS',
  type: 'text',
  label: translate('filters.Page url'),
  description: translate('filters.pageUrlDescription'),
  operators: matchOperators,
  multiline: true
}

const VIEWED_CATEGORY_ID_CONTAINS: Filter = {
  id: 'VIEWED_CATEGORY_ID_CONTAINS',
  type: 'text',
  label: translate('filters.Viewed category ID'),
  description: translate('filters.viewedCategoryIdDescription'),
  operators: matchOperators,
  multiline: true
}

const VIEWED_CATEGORY_NAME_CONTAINS: Filter = {
  id: 'VIEWED_CATEGORY_NAME_CONTAINS',
  type: 'text',
  label: translate('filters.Viewed category name contains'),
  description: translate('filters.viewedCategoryNameDescription'),
  operators: matchOperators,
  multiline: true
}

const VIEWED_PRODUCT_ID_CONTAINS: Filter = {
  id: 'VIEWED_PRODUCT_ID_CONTAINS',
  type: 'text',
  label: translate('filters.Viewed product ID contains'),
  description: translate('filters.viewedProductIdDescription'),
  operators: matchOperators,
  multiline: true
}

const VIEWED_PRODUCT_NAME_CONTAINS: Filter = {
  id: 'VIEWED_PRODUCT_NAME_CONTAINS',
  type: 'text',
  label: translate('filters.Viewed product name contains'),
  description: translate('filters.viewedProductNameDescription'),
  operators: matchOperators,
  multiline: true
}

const CART_CATEGORY_ID_CONTAINS: Filter = {
  id: 'CART_CATEGORY_ID_CONTAINS',
  type: 'text',
  label: translate('filters.Cart category ID contains'),
  description: translate('filters.cartCategoryIdDescription'),
  operators: matchOperators,
  multiline: true
}

const CART_CATEGORY_NAME_CONTAINS: Filter = {
  id: 'CART_CATEGORY_NAME_CONTAINS',
  type: 'text',
  label: translate('filters.Cart category name contains'),
  description: translate('filters.cartCategoryNameDescription'),
  operators: matchOperators,
  multiline: true
}

const CART_PRODUCT_ID_CONTAINS: Filter = {
  id: 'CART_PRODUCT_ID_CONTAINS',
  type: 'text',
  label: translate('filters.Cart product ID contains'),
  description: translate('filters.cartProductIdDescription'),
  operators: matchOperators,
  multiline: true
}

const CART_PRODUCT_NAME_CONTAINS: Filter = {
  id: 'CART_PRODUCT_NAME_CONTAINS',
  type: 'text',
  label: translate('filters.Cart product name contains'),
  description: translate('filters.cartProductNameDescription'),
  operators: matchOperators,
  multiline: true
}

const DOMAIN: Filter = {
  id: 'DOMAIN',
  type: 'list',
  label: translate('filters.Domain'),
  description: translate('filters.domainDescription'),
  operators: oneOfOperators,
  options(account) {
    return account.domains.map(d => ({
      value: d.domain,
      label: d.domain
    }))
  }
}

const BROWSER_LANGUAGE: Filter = {
  id: 'BROWSER_LANGUAGE',
  type: 'text',
  label: translate('filters.Browser language'),
  description: translate('filters.browserLanguageDescription'),
  operators: matchOperators,
  multiline: true
}

const SOURCE: Filter = {
  id: 'SOURCE',
  type: 'list',
  label: translate('filters.Source'),
  description: translate('filters.sourceDescription'),
  operators: oneOfOperators,
  options: [
    { value: 'direct', label: translate('filters.Direct') },
    { value: 'email', label: translate('filters.Email') },
    { value: 'ask', label: 'Ask.com' },
    { value: 'bing-organic', label: 'Bing Organic' },
    { value: 'google-organic', label: 'Google Organic' },
    { value: 'yahoo-organic', label: 'Yahoo Organic' },
    { value: 'yandex-organic', label: 'Yandex Organic' },
    { value: 'bing-paid', label: 'Bing Paid' },
    { value: 'criteo-paid', label: 'Criteo Paid' },
    { value: 'facebook-paid', label: 'Facebook Paid' },
    { value: 'google-paid', label: 'Google Paid' },
    { value: 'yahoo-paid', label: 'Yahoo Paid' },
    { value: 'facebook', label: 'Facebook' },
    { value: 'instagram', label: 'Instagram' },
    { value: 'reddit', label: 'Reddit' },
    { value: 'twitter', label: 'Twitter' },
    { value: 'vk', label: 'VKontakte' },
    { value: 'youtube', label: 'YouTube' },
    { value: 'web-push-notification', label: translate('filters.Web Push Notification') },
    { value: 'other', label: translate('filters.Other') }
  ]
}

const SOURCE_TYPE: Filter = {
  id: 'SOURCE_TYPE',
  type: 'list',
  label: translate('filters.Source type'),
  description: translate('filters.sourceTypeDescription'),
  operators: oneOfOperators,
  options: [
    { value: 'direct', label: translate('filters.Direct') },
    { value: 'email', label: translate('filters.Email') },
    { value: 'organic-search', label: translate('filters.Organic Search') },
    { value: 'paid-search', label: translate('filters.Paid Search') },
    { value: 'social', label: translate('filters.Social') },
    { value: 'notification', label: translate('filters.Notification') },
    { value: 'other', label: translate('filters.Other') }
  ]
}

const DEVICE: Filter = {
  id: 'DEVICE',
  type: 'list',
  label: translate('filters.Device'),
  description: translate('filters.deviceDescription'),
  operators: oneOfOperators,
  options: [
    { value: 'desktop', label: translate('filters.Desktop') },
    { value: 'tablet', label: translate('filters.Tablet') },
    { value: 'mobile', label: translate('filters.Mobile') }
  ]
}

const BROWSER: Filter = {
  id: 'BROWSER',
  type: 'list',
  label: translate('filters.Browser'),
  description: translate('filters.browserDescription'),
  operators: oneOfOperators,
  options: [
    { value: 'chrome', label: 'Chrome' },
    { value: 'firefox', label: 'Firefox' },
    { value: 'safari', label: 'Safari' },
    { value: 'ie', label: 'Internet Exporer' },
    { value: 'opera', label: 'Opera' }
  ]
}

const OS: Filter = {
  id: 'OS',
  type: 'list',
  label: translate('filters.Operating System'),
  description: translate('filters.operatingSystemDescription'),
  operators: oneOfOperators,
  options: [
    { value: 'windows', label: 'Windows' },
    { value: 'mac', label: 'Mac' },
    { value: 'linux', label: 'Linux' },
    { value: 'android', label: 'Android' }
  ]
}

const SEGMENTS: Filter = {
  id: 'SEGMENTS',
  type: 'list',
  label: translate('filters.Segments'),
  description: translate('filters.segmentsDescription'),
  operators: oneOfOperators,
  options(account) {
    return account.campaigns.reduce(
      (list: FilterOption[], campaign) => {
        campaign.segments.forEach(segment => {
          list.push({
            value: String(segment.id),
            label: `${segment.name} (${campaign.name})`,
            status: segment.status,
            campaignStatus: campaign.status
          })
        })
        return list
      },
      []
    )
  },
  reduceToAvailable(options) {
    return options.filter(opt => {
      return opt.campaignStatus === 'running' &&
        opt.status === 'active'
    })
  }
}

const WIDGET_ID: Filter = {
  id: 'WIDGET_ID',
  type: 'list',
  label: translate('filters.Widgets'),
  description: translate('filters.widgetsDescription'),
  operators: oneOfOperators,
  options(account) {
    return account.widgets.map(d => ({
      value: String(d.id),
      label: d.name
    }))
  }
}

const OFFER_PRESENTED: Filter = {
  id: 'OFFER_PRESENTED',
  type: 'list',
  label: translate('filters.offerPresented'),
  description: translate('filters.offerPresentedDescription'),
  operators: oneOfOperators,
  options(account) {
    return account.campaigns.reduce(
      (list: FilterOption[], campaign) => {
        campaign.incentives.forEach(offer => {
          list.push({
            value: String(offer.id),
            label: `${offer.displayText} (#${offer.id}, ${campaign.name})`,
            status: offer.status,
            campaignStatus: campaign.status
          })
        })
        return list
      },
      []
    )
  },
  reduceToAvailable(options) {
    return options.filter(opt => {
      return opt.campaignStatus === 'running' &&
        opt.status === 'active'
    })
  }
}

// const FORM_ID: Filter = {
//   id: 'FORM_ID',
//   type: 'text',
//   label: translate('filters.Form Identifier'),
//   description: translate('filters.formIdentifierDescription'),
//   operators: matchOperators,
//   multiline: true
// }

const TRIGGER_WIDGET_ID: Filter = {
  id: 'TRIGGER_WIDGET_ID',
  type: 'list',
  label: translate('filters.Trigger Source Widget'),
  description: translate('filters.triggerSourceWidgetDescription'),
  operators: oneOfOperators,
  options(account) {
    return account.widgets.map(d => ({
      value: String(d.id),
      label: d.name
    }))
  },
  canUse(doc) {
    const {triggerType} = doc.trigger
    return triggerType === 'EMAIL_COLLECTED' ||
      triggerType === 'FORM_SUBMISSION'
  }
}

const NUM_PAGE_VIEWS: Filter = {
  id: 'NUM_PAGE_VIEWS',
  type: 'text',
  label: translate('filters.Page Views'),
  description: translate('filters.pageViewsDescription'),
  operators: numericOperators,
  multiline: false
}

const NUM_CATEGORY_PAGE_VIEWS: Filter = {
  id: 'NUM_CATEGORY_PAGE_VIEWS',
  type: 'text',
  label: translate('filters.Category Page Views'),
  description: translate('filters.categoryPageViewsDescription'),
  operators: numericOperators,
  multiline: false
}

const NUM_PRODUCT_PAGE_VIEWS: Filter = {
  id: 'NUM_PRODUCT_PAGE_VIEWS',
  type: 'text',
  label: translate('filters.Product Page Views'),
  description: translate('filters.productPageViewsDescription'),
  operators: numericOperators,
  multiline: false
}

const NUM_CART_PAGE_VIEWS: Filter = {
  id: 'NUM_CART_PAGE_VIEWS',
  type: 'text',
  label: translate('filters.Cart Page Views'),
  description: translate('filters.cartPageViewsDescription'),
  operators: numericOperators,
  multiline: false
}

const TIME_ON_SITE: Filter = {
  id: 'TIME_ON_SITE',
  type: 'text',
  label: translate('filters.Seconds on Site'),
  description: translate('filters.secondsOnSiteDescription'),
  operators: numericOperators,
  multiline: false
}

const ABANDONED_CART_VALUE: Filter = {
  id: 'ABANDONED_CART_VALUE',
  type: 'text',
  label: translate('filters.Abandoned Cart Value'),
  description: translate('filters.abandonedCartValueDescription'),
  operators: numericOperators,
  multiline: false
}

const VISITS_SINCE_LAST_CONVERSION: Filter = {
  id: 'VISITS_SINCE_LAST_CONVERSION',
  type: 'text',
  label: translate('filters.Visits Since Last Conversion'),
  description: translate('filters.visitsSinceLastConversionDescription'),
  operators: numericOperators,
  multiline: false
}

const ORDER_VALUE: Filter = {
  id: 'ORDER_VALUE',
  type: 'text',
  label: translate('filters.Order Value'),
  description: translate('filters.orderValueDescription'),
  operators: numericOperators,
  multiline: false,
  canUse(doc) {
    return doc.trigger.triggerType === 'ORDER_RECEIVED'
  }
}

const HAS_NOTIFICATION_SUBSCRIPTION: Filter = {
  id: 'HAS_NOTIFICATION_SUBSCRIPTION',
  type: 'boolean',
  label: translate('filters.Has notification subscription'),
  description: translate('filters.hasNotificationSubscriptionDescription')
}

const HAS_SMS_SUBSCRIPTION: Filter = {
  id: 'HAS_SMS_SUBSCRIPTION',
  type: 'boolean',
  label: translate('filters.Has SMS subscription'),
  description: translate('filters.hasSmsSubscriptionDescription')
}

const HAS_EMAIL_SUBSCRIPTION: Filter = {
  id: 'HAS_EMAIL_SUBSCRIPTION',
  type: 'boolean',
  label: translate('filters.Has email subscription'),
  description: translate('filters.hasEmailSubscriptionDescription')
}

const USER_DATA_PROPERTY: Filter = {
  id: 'USER_DATA_PROPERTY',
  type: 'text',
  label: translate('filters.userDataPropertyLabel'),
  description: translate('filters.userDataPropertyDesc'),
  operators: [
    ...existanceOperators,
    ...numericOperators,
    ...matchOperators
  ],
  multiline: false,
  hasProperty: true
}

// Export all filters
/////////////////////////////////////////////////

export const filters: Record<string, Filter> = {
  PAGE_URL_CONTAINS,
  VIEWED_CATEGORY_ID_CONTAINS,
  VIEWED_CATEGORY_NAME_CONTAINS,
  VIEWED_PRODUCT_ID_CONTAINS,
  VIEWED_PRODUCT_NAME_CONTAINS,
  CART_CATEGORY_ID_CONTAINS,
  CART_CATEGORY_NAME_CONTAINS,
  CART_PRODUCT_ID_CONTAINS,
  CART_PRODUCT_NAME_CONTAINS,
  DOMAIN,
  BROWSER_LANGUAGE,
  SOURCE,
  SOURCE_TYPE,
  DEVICE,
  BROWSER,
  OS,
  SEGMENTS,
  WIDGET_ID,
  // FORM_ID,
  TRIGGER_WIDGET_ID,
  OFFER_PRESENTED,
  NUM_PAGE_VIEWS,
  NUM_CATEGORY_PAGE_VIEWS,
  NUM_PRODUCT_PAGE_VIEWS,
  NUM_CART_PAGE_VIEWS,
  TIME_ON_SITE,
  ABANDONED_CART_VALUE,
  VISITS_SINCE_LAST_CONVERSION,
  ORDER_VALUE,
  HAS_NOTIFICATION_SUBSCRIPTION,
  HAS_SMS_SUBSCRIPTION,
  HAS_EMAIL_SUBSCRIPTION,
  USER_DATA_PROPERTY
}
